import React from "react";
import { Header2, Body1, styled, borderRadius, modalPadding, modalPaddingExtraSmall, Header3, Body2 } from "@iventis/styles";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import { Theme } from "@emotion/react";
import { useTheme } from "@mui/material";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { SUPPORT_EMAIL } from "@iventis/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PulsatingLoading, useProgressTimer } from "./pulsating-loading";

export enum ConnectionStatus {
    PENDING = "Pending",
    SUCCESS = "Success",
    FAILURE = "Failure",
    SKIPPED = "Skipped",
}

export const NetworkStatusCard: React.FunctionComponent<{ title: string; subtitle: string; status: ConnectionStatus; action: string; timeoutTime: number; error?: string }> = ({
    title,
    subtitle,
    status,
    action,
    timeoutTime,
    error,
}) => {
    const theme = useTheme<Theme>();
    const translate = useIventisTranslate();

    const progress = useProgressTimer({
        status,
        isLoading: (status) => status === ConnectionStatus.PENDING,
        isComplete: (status) => status === ConnectionStatus.SUCCESS,
        durationMs: timeoutTime,
    });

    const statusColor = () => {
        switch (status) {
            case ConnectionStatus.SUCCESS:
                return theme.toastColours.success;
            case ConnectionStatus.FAILURE:
                return theme.toastColours.error;
            case ConnectionStatus.SKIPPED:
            case ConnectionStatus.PENDING:
            default:
                return theme.secondaryColors.grey;
        }
    };

    const getStatusIcon = () => {
        switch (status) {
            case ConnectionStatus.SUCCESS:
                return "check-circle";
            case ConnectionStatus.FAILURE:
                return "xmark-circle";
            case ConnectionStatus.SKIPPED:
                return "forward";
            case ConnectionStatus.PENDING:
            default:
                return "circle";
        }
    };

    return (
        <StyledCard style={{ borderColor: statusColor() }}>
            <HeaderContainer>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FontAwesomeIcon icon={getStatusIcon()} color={statusColor()} />
                    <Header2>{title}</Header2>
                </div>
                <Body1>{subtitle}</Body1>
            </HeaderContainer>
            {status === ConnectionStatus.PENDING ? (
                <LoadingContainer>
                    <Body2>{translate(Content.map12.digital_twin_network.testing)}</Body2>
                    <PulsatingLoading variant="determinate" value={Math.round(progress)} />
                </LoadingContainer>
            ) : (
                <>
                    <Chip
                        label={status}
                        style={{
                            backgroundColor: statusColor(),
                            color: theme.typographyColors.blank,
                        }}
                    />
                    {error && (
                        <>
                            <ErrorBody>{error}</ErrorBody>
                            <RecommendedActionsContainer>
                                <Header3>{translate(Content.map12.digital_twin_network.recommended_actions.title)}</Header3>
                                <Body1>
                                    {action} {SUPPORT_EMAIL}
                                </Body1>
                            </RecommendedActionsContainer>
                        </>
                    )}
                </>
            )}
        </StyledCard>
    );
};

const ErrorBody = styled(Body1)`
    color: ${(props) => props.theme.secondaryColors.error};
`;

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${modalPaddingExtraSmall};
`;

const StyledCard = styled(Card)`
    padding: ${modalPaddingExtraSmall};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${modalPadding};
    width: 100%;
    max-width: 600px;
    min-width: 280px;
    border-radius: ${borderRadius.standard};
    border: 1px solid ${(props) => props.theme.secondaryColors.error};
    box-sizing: border-box;
`;

const RecommendedActionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${modalPaddingExtraSmall};
    background-color: ${(props) => props.theme.tertiaryColors.primaryBackground};
    border: 1px solid ${(props) => props.theme.tertiaryColors.primary};
    border-radius: ${borderRadius.standard};
    width: 100%;
    padding: ${modalPaddingExtraSmall};
    box-sizing: border-box;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
